import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Material UI
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import theme from './theme/theme';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Page Components
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';

// Inventory Components
import InventoryList from './pages/inventory/InventoryList';
import PartDetails from './pages/inventory/PartDetails';
import InventoryAdjust from './pages/inventory/InventoryAdjust';
import NewPart from './pages/inventory/NewPart';
import WarehouseReorderBulkUpdate from './pages/inventory/WarehouseReorderBulkUpdate';
import Scan from './pages/inventory/Scan';
import PhysicalInventory from './pages/inventory/PhysicalInventory';
import PhysicalInventoryReport from './pages/inventory/PhysicalInventoryReport';
import PhysicalInventoryList from './pages/inventory/PhysicalInventoryList';

// Supplier Components
import SupplierList from './pages/suppliers/SupplierList';
import SupplierDetails from './pages/suppliers/SupplierDetails';
import AddSupplier from './pages/suppliers/AddSupplier';
import EditSupplier from './pages/suppliers/EditSupplier';
import AddPartToSupplier from './pages/suppliers/AddPartToSupplier';

// Location Components
import LocationList from './pages/locations/LocationList';
import LocationForm from './pages/locations/LocationForm';
import LocationInventory from './pages/locations/LocationInventory';
import WarehouseForm from './pages/locations/WarehouseForm';
import WarehouseList from './pages/locations/WarehouseList';

// Report Components
import InventoryReport from './pages/reports/InventoryReport';
import ReorderReport from './pages/reports/ReorderReport';
import TransactionHistoryReport from './pages/reports/TransactionHistoryReport';
import SupplierMatrixReport from './pages/reports/SupplierMatrixReport';

// Admin Components
import UserManagement from './pages/admin/UserManagement';

// Technician Components
import TechniciansList from './pages/technicians/TechniciansList';
import TechnicianInventory from './pages/technicians/TechnicianInventory';
import TechnicianInventorySummary from './pages/technicians/TechnicianInventorySummary';

// QR Scan Component
import QRScan from './pages/QRScan';

import NotFound from './pages/NotFound';

// Context
import AuthContext, { AuthProvider } from './context/AuthContext';

function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Header />
          <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: 'background.default' }}>
            <ToastContainer position="top-right" autoClose={3000} />
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />

            {/* Inventory Routes */}
            <Route path="/inventory" element={<PrivateRoute><InventoryList /></PrivateRoute>} />
            <Route path="/inventory/adjust" element={<PrivateRoute><InventoryAdjust /></PrivateRoute>} />
            <Route path="/inventory/scan" element={<PrivateRoute><Scan /></PrivateRoute>} />
            <Route path="/inventory/physical" element={<PrivateRoute><PhysicalInventory /></PrivateRoute>} />
            <Route path="/inventory/physical/list" element={<PrivateRoute><PhysicalInventoryList /></PrivateRoute>} />
            <Route path="/inventory/physical/:id" element={<PrivateRoute><PhysicalInventory /></PrivateRoute>} />
            <Route path="/inventory/physical/:id/discrepancies" element={<PrivateRoute><PhysicalInventoryReport /></PrivateRoute>} />
            <Route path="/inventory/new" element={<PrivateRoute requireRole="purchaser"><NewPart /></PrivateRoute>} />
            <Route path="/inventory/reorder-bulk" element={<PrivateRoute requireRole="purchaser"><WarehouseReorderBulkUpdate /></PrivateRoute>} />
            <Route path="/inventory/:id" element={<PrivateRoute><PartDetails /></PrivateRoute>} />

            {/* Supplier Routes */}
            <Route path="/suppliers" element={<PrivateRoute><SupplierList /></PrivateRoute>} />
            <Route path="/suppliers/new" element={<PrivateRoute requireRole="purchaser"><AddSupplier /></PrivateRoute>} />
            <Route path="/suppliers/:id" element={<PrivateRoute><SupplierDetails /></PrivateRoute>} />
            <Route path="/suppliers/edit/:id" element={<PrivateRoute requireRole="purchaser"><EditSupplier /></PrivateRoute>} />
            <Route path="/suppliers/:id/add-part" element={<PrivateRoute requireRole="purchaser"><AddPartToSupplier /></PrivateRoute>} />

            {/* Location Routes */}
            <Route path="/locations" element={<PrivateRoute><LocationList /></PrivateRoute>} />
            <Route path="/locations/add" element={<PrivateRoute requireRole="manager"><LocationForm /></PrivateRoute>} />
            <Route path="/locations/edit/:id" element={<PrivateRoute requireRole="manager"><LocationForm /></PrivateRoute>} />
            <Route path="/locations/:id/inventory" element={<PrivateRoute><LocationInventory /></PrivateRoute>} />
            <Route path="/warehouses" element={<PrivateRoute><WarehouseList /></PrivateRoute>} />
            <Route path="/warehouses/add" element={<PrivateRoute requireRole="manager"><WarehouseForm /></PrivateRoute>} />
            <Route path="/warehouses/edit/:id" element={<PrivateRoute requireRole="manager"><WarehouseForm /></PrivateRoute>} />

            {/* Report Routes */}
            <Route path="/reports/inventory" element={<PrivateRoute requireRole="purchaser"><InventoryReport /></PrivateRoute>} />
            <Route path="/reports/reorder" element={<PrivateRoute requireRole="purchaser"><ReorderReport /></PrivateRoute>} />
            <Route path="/reports/history" element={<PrivateRoute requireRole="purchaser"><TransactionHistoryReport /></PrivateRoute>} />
            <Route path="/reports/supplier-matrix" element={<PrivateRoute requireRole="purchaser"><SupplierMatrixReport /></PrivateRoute>} />

            {/* Technician Routes */}
            <Route path="/technicians" element={<PrivateRoute><TechniciansList /></PrivateRoute>} />
            <Route path="/technician-inventory" element={<PrivateRoute><TechnicianInventory /></PrivateRoute>} />
            <Route path="/technicians/:technicianId/inventory" element={<PrivateRoute><TechnicianInventorySummary /></PrivateRoute>} />

            {/* QR Scan Route - Public access for mobile scanning */}
            <Route path="/qr-scan" element={<QRScan />} />

            {/* Admin Routes */}
            <Route path="/users" element={<PrivateRoute requireRole="manager"><UserManagement /></PrivateRoute>} />

            <Route path="*" element={<NotFound />} />
            </Routes>
          </Box>
          <Footer />
        </Box>
      </ThemeProvider>
    </AuthProvider>
  );
}

// Private route component
const PrivateRoute = ({ children, requireRole }) => {
  const { isAuthenticated, user, loading } = React.useContext(AuthContext);
  const [initialPath] = React.useState(window.location.pathname);

  // Store the current path in session storage when component mounts
  React.useEffect(() => {
    if (!isAuthenticated && !loading) {
      sessionStorage.setItem('redirectPath', initialPath);
    }
  }, [isAuthenticated, loading, initialPath]);

  // Show loading indicator while checking authentication
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (requireRole && (!user || (user.role !== requireRole && user.role !== 'manager'))) {
    return <Navigate to="/" />;
  }

  return children;
};

export default App;
